<template>
  <div ref="scribbleRef" class="c-scribble w-full">
    <PlaceholderComponent v-if="shouldDisplayPlaceholderComponent(isEditMode, mode, visible)" :name="tracking_id" />
    <EditableComponent
      v-else-if="isEditMode && !isViewOnlyMode && componentPermissions.hasSomethingEditable"
      :id="id"
      :add="componentPermissions.canEdit"
      :duplicate="componentPermissions.canDuplicate"
      :excludeStylables="[ThemeComponentOptionEnum.PADDING]"
      :remove="componentPermissions.canRemove"
      :selected="componentSelectedMode && componentSelectedMode?.id === id"
      :settings="!usedInSectionId"
      :stylable="!usedInSectionId"
      @add="onAdd(id)"
      @duplicate="duplicate(id)"
      @remove="removeComponentById(id)"
      @select="setComponentSelectedMode(id)"
      @settings="setComponentSettingsMode(id)"
      @style="emit('style', $event)"
    >
      <div
        ref="scrollableContainerRef"
        class="overflow-y-hidden"
        :class="{
          'overflow-x-hidden': !shouldShowHorizontalScroll,
          'overflow-x-scroll': shouldShowHorizontalScroll,
        }"
      >
        <Draggable
          ghost-class="ghost-class"
          handle=".drag-handle"
          item-key="id"
          :model-value="children"
          @dragend="clearState"
          @dragleave="onDragLeave"
          @dragover="onDragOver"
          @dragstart="onDragStart"
          @drop.prevent.stop="onDrop"
        >
          <div
            ref="containerRef"
            class="scribble-container relative overflow-hidden rounded-2 bg-primary6"
            :style="containerStyle"
          >
            <div ref="dropPreviewRef" :style="dropPreviewStyle" />
            <template v-if="!children?.length && !style?.backgroundImage">
              <div
                class="flex justify-center items-center b-rounded-2 cursor-pointer w-full h-full pa-8"
                @mousedown.stop="onMouseDown($event, id)"
              >
                <CButton circle secondary size="large" strong>
                  <template #icon>
                    <CIcon icon="plus-large" size="16px" />
                  </template>
                </CButton>
              </div>
            </template>
            <template v-else>
              <div
                v-for="component in filteredChildren"
                :id="component.id"
                :key="component.id"
                v-element-visibility="(isVisible: boolean) => onElementVisibility(isVisible, component)"
                :style="getComponentPositionStyle(component)"
              >
                <LinkableComponent :id="component.id" no-border no-padding :used-in-section-id="usedInSectionId">
                  <Component
                    :is="componentMap[component.type as ComponentTypes]"
                    :parent-type="ComponentTypes.Scribble"
                    v-bind="omit(component, ['type', 'children', 'style'])"
                    :style="omit(component.style || {}, ['position', 'left', 'top', 'zIndex'])"
                    :used-in-section-id="usedInSectionId"
                    @style="(s: Record<string, any>) => emit('style', s, component.id)"
                  />
                </LinkableComponent>
              </div>
            </template>
          </div>
        </Draggable>
      </div>
    </EditableComponent>

    <div
      v-else
      ref="scrollableContainerRef"
      :class="{
        'overflow-x-hidden': !shouldShowHorizontalScroll,
        'overflow-x-scroll': shouldShowHorizontalScroll,
      }"
    >
      <div ref="containerRef" class="scribble-container relative overflow-hidden bg-primary6" :style="containerStyle">
        <div
          v-for="component in filteredChildren"
          :key="component.id"
          v-element-visibility="(isVisible: boolean) => onElementVisibility(isVisible, component)"
          :style="getComponentPositionStyle(component)"
        >
          <LinkableComponent :id="component.id" no-border no-padding :used-in-section-id="usedInSectionId">
            <Component
              :is="componentMap[component.type as ComponentTypes]"
              :parent-type="ComponentTypes.Scribble"
              v-bind="omit(component, ['type', 'children', 'style'])"
              :style="omit(component.style || {}, ['position', 'left', 'top', 'zIndex'])"
              :used-in-section-id="usedInSectionId"
            />
          </LinkableComponent>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { vElementVisibility } from '@vueuse/components'
import { useElementSize, useSwipe } from '@vueuse/core'
import { debounce, omit } from 'lodash-es'
import {
  toRefs,
  computed,
  ref,
  watch,
  type ComputedRef,
  type CSSProperties,
  onMounted,
  onBeforeUnmount,
  nextTick,
  inject,
} from 'vue'
import { VueDraggable as Draggable } from 'vue-draggable-plus'

import CButton from '@lib/components/CButton/CButton.vue'
import LinkableComponent from '@canvas-builder/components/container/LinkableComponent/LinkableComponent.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import DynamicDataTable from '@canvas-builder/components/ui/DynamicDataTable/DynamicDataTable.vue'
import EditableComponent from '@canvas-builder/components/container/EditableComponent/EditableComponent.vue'
import Text from '@canvas-builder/components/ui/Text/Text.vue'
import Multimedia from '@canvas-builder/components/ui/Multimedia/Multimedia.vue'
import {
  type ComponentTrackingParams,
  type ComponentNode,
  type ScribbleProps,
  ComponentTypes,
  type ComponentNodeThemeMeta,
  type CanvasTheme,
} from '@canvas-builder/types/canvas'
import { defaultWhenUsedInSection } from '@canvas-builder/constants'
import { type CanvasRetrieve } from '@openapi'
import useComponentPermissions from '@canvas-builder/composables/useComponentPermissions'
import { shouldDisplayPlaceholderComponent } from '@canvas-builder/util/canvas.util'
import PlaceholderComponent from '@canvas-builder/components/container/PlaceholderComponent/PlaceholderComponent.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasTheme, { ThemeComponentOptionEnum } from '@canvas-builder/composables/useCanvasTheme'
import { useComponentStyle } from '@canvas-builder/composables/useComponentStyle'
import { calculateProportionalDimensions, ACTUAL_CANVAS_WIDTH, getComponentPositionStyle } from './scribble.helpers'

import { extractUrlFromCssBackgroundImage } from '@canvas-builder/util/url'
import type { BackgroundPositionX, BackgroundPositionY, BackgroundRepeat, BackgroundSize } from '@lib/types/background'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import useReorderComponent from '@canvas-builder/composables/useReorderComponent'

const props = withDefaults(
  defineProps<{
    id: string
    data: ScribbleProps
    children?: ComponentNode[]
    usedInSectionId?: CanvasRetrieve['id']
    when_used_in_section?: ComponentNode['when_used_in_section']
    visible?: ComponentNode['visible']
    tracking_id?: string
    style?: CSSProperties
    theme_meta?: ComponentNodeThemeMeta
  }>(),
  {
    children: () => [],
    when_used_in_section: () => defaultWhenUsedInSection,
    usedInSectionId: '',
  },
)

const emit = defineEmits<{
  style: [s: CSSProperties, id?: ComponentNode['id']]
}>()

const { id, usedInSectionId, when_used_in_section } = toRefs(props)

const {
  componentNodesById,
  componentSelectedMode,
  duplicate,
  isEditMode,
  isViewOnlyMode,
  mode,
  onComponentEnter,
  onComponentExit,
  removeComponentById,
  setAddMode,
  setComponentSelectedMode,
  setComponentSettingsMode,
  activeCanvasSections,
} = useCanvas()
const { isFluid } = useCanvasTheme()
const themeVars = useThemeVars()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const componentMap = computed(() => {
  const baseComponents: Partial<Record<ComponentTypes, any>> = {
    Multimedia,
    Text,
  }

  // Only include DynamicDataTable if the feature flag is enabled
  if (launchDarkly.value?.allow_dynamic_data_table_for_scribble_component) {
    baseComponents.DynamicDataTable = DynamicDataTable
  }

  return baseComponents
})

const componentPermissions = useComponentPermissions({
  id,
  usedInSectionId,
  when_used_in_section,
})

const scribbleRef = ref<HTMLElement | null>(null)
const containerRef = ref<HTMLElement | null>(null)
const scrollableContainerRef = ref<HTMLElement | null>(null)
const isDragging = ref(false)
const dropPreviewRef = ref<HTMLDivElement | null>(null)
const dropPosition = ref<{ x: number; y: number } | null>(null)
const isDraggingInside = ref(false)
const defaultBackgroundStyles: {
  backgroundPositionX: BackgroundPositionX
  backgroundPositionY: BackgroundPositionY
  backgroundSize: BackgroundSize
  backgroundRepeat: BackgroundRepeat
} = {
  backgroundPositionX: 'left',
  backgroundPositionY: 'top',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
}

const { width: scribbleWidth } = useElementSize(scribbleRef)
const { direction } = useSwipe(scribbleRef, {
  onSwipe(e: TouchEvent) {
    if (direction.value === 'left' || direction.value === 'right') {
      e.preventDefault()
      e.stopPropagation()
    }
  },
})

const { getFilteredScribbleChildren } = useReorderComponent()

const activeChild = computed(() =>
  componentSelectedMode.value ? (componentNodesById.value[componentSelectedMode.value.id] as ComponentNode) : null,
)

const componentNode = computed(() => (props.id ? componentNodesById.value[props.id] : null) as ComponentNode)

const filteredChildren = computed(() => getFilteredScribbleChildren(props.id, props.children))

const { createStyleUpdate: createActiveChildStyleUpdate } = useComponentStyle(activeChild as ComputedRef<ComponentNode>)
const { createStyleUpdate: createComponentNodeStyleUpdate } = useComponentStyle(componentNode)

const { getDerivedStyleFromFeatureFlags, getCssThemeMetaStyleOverrides } = useComponentStyle()

const sectionTheme = computed(() => {
  return usedInSectionId.value ? (activeCanvasSections.value[usedInSectionId.value]?.theme as CanvasTheme) : undefined
})

const containerStyle = computed(() => {
  const baseStyle = {
    backgroundImage: props.style?.backgroundImage,
    backgroundColor: props.style?.backgroundColor,
    backgroundPositionX: props.style?.backgroundPositionX,
    backgroundPositionY: props.style?.backgroundPositionY,
    backgroundRepeat: props.style?.backgroundRepeat,
    backgroundSize: props.style?.backgroundSize,
    height: !props.children?.length && !props.style?.height ? 'auto' : props.style?.height || '500px',
    marginLeft: isFluid.value ? 'auto' : undefined,
    marginRight: isFluid.value ? 'auto' : undefined,
    maxWidth:
      isFluid.value && !props.children?.length && !props.style?.backgroundImage
        ? `${ACTUAL_CANVAS_WIDTH}px`
        : undefined,
    width:
      !props.children?.length && !props.style?.backgroundImage
        ? 'auto'
        : props.style?.width || `${ACTUAL_CANVAS_WIDTH}px`,
  }

  const styleWithFeatureFlags = {
    ...getDerivedStyleFromFeatureFlags(baseStyle),
    ...getCssThemeMetaStyleOverrides(ComponentTypes.Scribble, props.theme_meta, sectionTheme.value),
  }

  return styleWithFeatureFlags
})

const shouldShowHorizontalScroll = computed(() => {
  if (!scribbleRef.value || !props.style?.width) return false
  const styleWidth = parseInt(props.style.width as string)

  return !isNaN(styleWidth) && styleWidth > scribbleWidth.value
})

const dropPreviewStyle: ComputedRef<CSSProperties> = computed(() => {
  if (!dropPosition.value) return { display: 'none' }

  return {
    position: 'absolute',
    left: `${dropPosition.value.x}px`,
    top: `${dropPosition.value.y}px`,
    pointerEvents: 'none',
    zIndex: 1000,
    opacity: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
    border: `2px dashed ${themeVars.value.primary}`,
    width: '200px',
    height: '80px',
    borderRadius: '4px',
  }
})

const DRAG_THROTTLE = 2
const KEYBOARD_POSITION_ADJUSTMENT_STEP = 1 // 1px movement per key press
const KEYBOARD_POSITION_ADJUSTMENT_STEP_LARGE = 10 // 10px movement for Shift+arrow
const WINDOW_RESIZE_DEBOUNCE_TIME = 50

const debouncedUpdateDropPosition = debounce((clientX: number, clientY: number, container: HTMLElement) => {
  const containerBounds = container.getBoundingClientRect()
  dropPosition.value = {
    x: clientX - containerBounds.left,
    y: clientY - containerBounds.top,
  }
}, DRAG_THROTTLE)

function centerScrollPosition() {
  if (!scrollableContainerRef.value || !containerRef.value || !shouldShowHorizontalScroll.value) return

  const containerWidth = parseInt((props.style?.width as string) || `${ACTUAL_CANVAS_WIDTH}`)
  const scrollableWidth = scrollableContainerRef.value.clientWidth

  if (containerWidth > scrollableWidth) {
    const scrollOffset = (containerWidth - scrollableWidth) / 2
    scrollableContainerRef.value.scrollLeft = scrollOffset
  }
}

const debouncedCenterScrollPosition = debounce(centerScrollPosition, WINDOW_RESIZE_DEBOUNCE_TIME)

function clearState() {
  isDragging.value = false
  isDraggingInside.value = false
  dropPosition.value = null
  debouncedUpdateDropPosition.cancel() // Cancel any pending updates
}

function onAdd(componentId?: ComponentNode['id']) {
  if (componentSelectedMode.value && componentSelectedMode.value?.id === componentId) {
    setAddMode(ComponentTypes.Scribble, componentId)
  } else {
    setComponentSelectedMode(componentId)
  }
}

function onDragLeave() {
  isDraggingInside.value = false
  dropPosition.value = null
}

function onDragStart(event: DragEvent) {
  if (!event.dataTransfer) return
  isDragging.value = true

  const dragImage = document.createElement('div')
  dragImage.style.width = '100px'
  dragImage.style.height = '100px'
  dragImage.style.opacity = '0'
  document.body.appendChild(dragImage)

  event.dataTransfer.setDragImage(dragImage, 0, 0)

  setTimeout(() => {
    document.body.removeChild(dragImage)
  }, 0)
}

function onDragOver(event: DragEvent) {
  if (!containerRef.value || !event.clientX || !event.clientY) return
  event.preventDefault()

  isDraggingInside.value = true
  debouncedUpdateDropPosition(event.clientX, event.clientY, containerRef.value)
}

function onElementVisibility(isVisible: boolean, c: ComponentNode) {
  const params: ComponentTrackingParams = { trackable: false }
  if (isVisible) onComponentEnter.value?.(c, params)
  else onComponentExit.value?.(c, params)
}

function onMouseDown(e: MouseEvent, componentId: ComponentNode['id']) {
  if (e.button !== 0) return
  onAdd(componentId)
}

function onDrop(event: DragEvent) {
  if (!containerRef.value || !event.clientX || !event.clientY || !isDraggingInside.value) {
    clearState()
    return
  }

  const containerBounds = containerRef.value.getBoundingClientRect()
  const left = event.clientX - containerBounds.left
  const top = event.clientY - containerBounds.top

  if (activeChild.value && props.children?.find((child) => child.id === activeChild.value?.id)) {
    emit(
      'style',
      createActiveChildStyleUpdate({
        position: 'absolute',
        left: `${left}px`,
        top: `${top}px`,
      }),
      activeChild.value.id,
    )
  }

  clearState()
}

function handleKeyboardNavigation(event: KeyboardEvent) {
  // Only process if in edit mode and not in view-only mode
  if (!isEditMode.value || isViewOnlyMode.value) return

  // Only process if we have a selected component that's a child of this scribble
  if (!componentSelectedMode.value || !activeChild.value) return
  if (!props.children?.find((child) => child.id === activeChild.value?.id)) return

  // Only handle arrow keys
  if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return

  // Skip if the active element is an input, textarea, or contenteditable
  // This prevents conflicts with text editing in the rich text editor
  const activeElement = document.activeElement
  if (
    activeElement instanceof HTMLInputElement ||
    activeElement instanceof HTMLTextAreaElement ||
    activeElement?.getAttribute('contenteditable') === 'true' ||
    activeElement?.closest('[contenteditable="true"]')
  ) {
    return
  }

  // Prevent default behavior and stop propagation to other containers
  // Use capture phase to intercept the event before it reaches other handlers
  event.preventDefault()
  event.stopPropagation()
  event.stopImmediatePropagation()

  const currentStyle = activeChild.value.style || {}
  const currentLeft = parseInt((currentStyle.left as string) || '0')
  const currentTop = parseInt((currentStyle.top as string) || '0')

  const step = event.shiftKey ? KEYBOARD_POSITION_ADJUSTMENT_STEP_LARGE : KEYBOARD_POSITION_ADJUSTMENT_STEP

  let newLeft = currentLeft
  let newTop = currentTop

  switch (event.key) {
    case 'ArrowUp':
      newTop -= step
      break
    case 'ArrowDown':
      newTop += step
      break
    case 'ArrowLeft':
      newLeft -= step
      break
    case 'ArrowRight':
      newLeft += step
      break
  }

  emit(
    'style',
    createActiveChildStyleUpdate({
      position: 'absolute',
      left: `${newLeft}px`,
      top: `${newTop}px`,
    }),
    activeChild.value.id,
  )
}

onMounted(() => {
  window.addEventListener('keydown', handleKeyboardNavigation, true)

  // initially, center scroll position
  nextTick(() => {
    debouncedCenterScrollPosition()
  })

  window.addEventListener('resize', debouncedCenterScrollPosition)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyboardNavigation, true)
  window.removeEventListener('resize', debouncedCenterScrollPosition)
  debouncedCenterScrollPosition.cancel() // Cancel any pending debounced calls
})

watch(
  () => props.style?.backgroundImage,
  async (newBackgroundImage) => {
    if (!newBackgroundImage) return
    const imageUrl = extractUrlFromCssBackgroundImage(newBackgroundImage) ?? ''
    const img = new Image()
    img.src = imageUrl
    try {
      await new Promise((resolve, reject) => {
        img.onload = () => {
          const { width, height } = calculateProportionalDimensions(img.naturalWidth, img.naturalHeight)

          emit(
            'style',
            createComponentNodeStyleUpdate({
              ...defaultBackgroundStyles,
              height: `${height}px`,
              width: `${width}px`,
            }),
          )

          resolve(null)
        }
        img.onerror = reject
      })
    } catch (error) {
      console.error('Error loading image:', error)
    }
  },
)
</script>

<style scoped>
.ghost-class {
  opacity: 0.8;
  position: absolute;
  pointer-events: none;
  z-index: 1000;
}

.ghost-class * {
  pointer-events: none;
}

.scribble-container {
  position: relative;
}
</style>
